import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from "../../../redux/reducer/store";
import { actionAnalyticVisit } from '../../../redux/action/charts/actionCharts';
import { reducerChangeDataCharts, reducerEditTypeChart } from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';

import {
  createFormatDateFunction,
  filteredZeroData,
  reverseChartsList,
  lineChartList,
  monthOptions,
  defaultFilter,
  normList,
  lineList,
  tickData,
  getColorOptions
} from '../../../utils/chartsData';
import { IChartOptions, IFiltersCharts, IContextOptions } from '../../../types/chartsInterface';
import { formatDateForMapReport } from '../../../utils/formatDate';

import "../charts.scss";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement
);

const typesCharts = [
  { value: 'bar', label: 'Гистограмма' },
  { value: 'barStacked', label: 'Гистограмма с накоплением' },
  { value: 'barStackedNorm', label: 'Нормированная гистограмма с накоплением' },
  { value: 'lineBar', label: 'Линейчатая' },
  { value: 'lineBarStacked', label: 'Линейчатая с накоплением' },
  { value: 'lineBarNorm', label: 'Нормированная линейчатая с накоплением' },
  { value: 'lineChart', label: 'График' },
  { value: 'lineChartStacked', label: 'График с накоплением' },
  { value: 'lineChartStackedNorm', label: 'Нормированный график с накоплением' },
  { value: 'pie', label: 'Круговая диаграмма' },
  { value: 'doughnut', label: 'Кольцевая диаграмма' },
];

const barTypes = ['bar', 'barStacked', 'barStackedNorm'];

const VisitsByDaysChart: FC<IChartOptions> = ({ pageId, id, copyChart, isCopy }) => {

  const dispatch: any = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const scrollStep = 21.5;

  const {
    pending,
    desktopData,
    settingCharts,
    desktopInputValues,
  } = useSelector((state: any) => state.charts?.chartsList);

  const {
    filters,
    areasOptions,
    chainsOptions,
    projectOptions,
    regionsOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const ticks = {
    ...tickData,
    color: darkTheme.isDarkTheme ? 'white' : 'black'
  };

  const index = desktopData[pageId]?.find((el: { id: number }) => el.id === id) || {};

  const {
    dataVisitCharts,
    dataPrecent,
    dataCircle,
    visitChart,
    periodList,
    labels,
    type
  } = index;

  const colorList = getColorOptions(settingCharts?.visit);
  const visitFilters = desktopInputValues?.[Number(pageId)]?.[id] || null;

  const [isListRightFiltersPosition, setIsListRightFiltersPosition] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] = useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] = useState<boolean>(false);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isData, setIsData] = useState([]);
  const typeCircle = type === 'pie' || type === 'doughnut';

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      }
    },
    {
      formatDate: createFormatDateFunction(contractsOptions)
    },
    {
      formatDate: createFormatDateFunction(projectOptions)
    },
    {
      formatDate: createFormatDateFunction(chainsOptions)
    },
    {
      formatDate: createFormatDateFunction(areasOptions)
    },
    {
      formatDate: createFormatDateFunction(regionsOptions)
    },
    {
      formatDate: createFormatDateFunction(monthOptions)
    },
  ];

  const data = {
    labels: isPeriod,
    datasets: isData,
  };

  const optionsForCircle = {
    responsive: true,
    layout: {
      padding: {
        bottom: 10,
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            if (tooltipItem) {
              const dataset = tooltipItem.dataset.data;
              const currentValue = tooltipItem.raw as number;
              const total = dataset.reduce((acc: number, value: number) => acc + value, 0);
              const percentage = ((currentValue / total) * 100).toFixed(2);
              return `${labels[tooltipItem.dataIndex]}: ${percentage}%`;
            }
          }
        }
      },
      datalabels: {
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          const datasets = context.chart.data.datasets[0].data;
          const firstDataset = datasets[0];
          const secondDataset = datasets[1];
          const total = firstDataset + secondDataset;
          const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
          return percentage === 0 ? '' : `${percentage}%`;
        },
        color: '#000',
        offset: isIncreasedSize ? -90 : -13,
        display: true,
        layout: {
          padding: {
            top: 20,
            bottom: 20,
          }
        }
      },
    },
  };

  const optionsForBar = {
    responsive: true,
    indexAxis: lineList.includes(type)
      ? 'y' as const
      : 'x' as const,
    layout: {
      padding: {
        top: 30,
        right: lineList.includes(type) ? 20 : 0,
        left: 0
      }
    },
    scales: {
      x: {
        stacked: !['bar', 'lineBar', 'lineChart'].includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black'
        }
      },
      y: {
        stacked: !['bar', 'lineBar', 'lineChart'].includes(type),
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black'
        }
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            if (tooltipItem) {
              const label = tooltipItem ? tooltipItem.dataset.label : '';
              const dataset = tooltipItem ? tooltipItem.formattedValue : '';
              return `${label}: ${dataset}${normList.includes(type) ? '%' : ''}`;
            }
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        color: '#000',
        align: 'end' as const,
        anchor: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.height._to;
            const widthSize = value.toString().length * 10;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return !normList.includes(type) ? value : value > 3 ? `${value}%` : '';
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
        size: 1,
        offset: barTypes.includes(type) ? -15 : lineList.includes(type) ? -7 : -3,
      },
    },
  };

  const actualOptionsChart = typeCircle ? optionsForCircle : optionsForBar;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in defaultFilter) {
      if (defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        if (JSON.stringify(defaultFilter[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        changes[key] = data[key];
      }
    };
    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(reducerChangeDataCharts({ hasChanged: Object.keys(changes).length > 0, changes }));
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(visitFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter(item => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  useEffect(() => {
    if (visitFilters) {
      getChangeData(visitFilters);
    }
    // eslint-disable-next-line
  }, [visitFilters]);

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = visitChart
          ? typeCircle
            ? dataCircle ? [dataCircle] : []
            : normList.includes(type) ? dataPrecent : dataVisitCharts
          : [];
        const directionData = reverseChartsList.includes(type) ? updatedData.slice().reverse() : updatedData;
        setIsVisiblelabel(typeCircle ? labels : filteredZeroData(updatedData));
        setIsData(directionData);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, normList, type]);

  useEffect(() => {
    const period = periodList && visitFilters ? periodList.map((el: string) => {
      if (visitFilters?.group.value === '7') {
        const parts = el.split('W');
        return parseInt(parts[1], 10);
      } else if (visitFilters?.group.value === '8') {
        return axisX[6].formatDate(el);
      } else if (visitFilters?.group.value === '9') {
        return el;
      } else {
        return axisX[visitFilters?.group?.value].formatDate(el);
      }
    }) : [];
    setIsPeriod(period);
    // eslint-disable-next-line
  }, [periodList])

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(list.right + 505 > viewportWidth);
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    if (filters) {
      setIsloader(true);
      dispatch(actionAnalyticVisit(id, pageId)).then(() => {
        setIsloader(false);
      });
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId || 0]?.includes(id) ? true : false);
    }
    // eslint-disable-next-line
  }, [pending]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      if (legendRef.current) {
        const currentScrollTop = legendRef.current.scrollTop;
        const newScrollTop =
          event.deltaY > 0
            ? currentScrollTop + scrollStep
            : currentScrollTop - scrollStep;

        legendRef.current.scrollTo({
          top: newScrollTop,
          behavior: 'smooth',
        });
      }
    };

    const legendElement = legendRef.current;

    if (legendElement) {
      legendElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (legendElement) {
        legendElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scrollStep, isloader]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`content-charts ${isIncreasedSize ? 'content-charts_active' : ''}`}>
      <div className='charts__header'>
        <p className='charts__title'>{`Визиты (План/Факт) ${isCopy ? '(дубль)' : ''}`}</p>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && <IncreaseIcon className='charts__icon' onClick={() => setIsIncreasedSize(true)} />}
            {isIncreasedSize && <ReduceIcon className='charts__icon' onClick={() => setIsIncreasedSize(false)} />}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content=" "
              purpose="filtersCharts"
              modalTitle="Настройка отображения данных"
              selectId={id}
              action={actionAnalyticVisit}
              inputValuesCharts={visitFilters}
            />
            {(!isChangeData || !visitFilters) &&
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
            {isChangeData && visitFilters &&
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
          </div>
          <div ref={listRef}>
            <SettingIcon className='charts__icon' onClick={handleClickSetting} />
            {isOpenSetting &&
              <ul className={`charts__setting ${isListRightPosition ? 'charts__setting_right' : ''}`}>
                <li onClick={() => {
                  handleClickSetting();
                  copyChart(id);
                }}>Дублировать</li>
                <li>
                  <HeaderBtn
                    content="Удалить"
                    purpose="removeCharts"
                    modalTitle="Удалить диаграмму?"
                    selectId={id} />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts &&
                    <ul className={`charts__setting charts__setting_list ${isListRightFiltersPosition ? 'charts__setting_list_typeRight' : ''}`}>
                      {typesCharts.map((el: { value: string, label: string }, index: number) => (
                        <li
                          key={index}
                          className={type === el.value ? 'item_active' : ''}
                          onClick={() => handleClickFilterOnType(el.value, index)}
                        >{el.label}</li>
                      ))}
                    </ul>}
                </li>
                <li>
                  <HeaderBtn
                    content="Скриншот"
                    purpose="screenCharts"
                    modalTitle="Сделать скриншот"
                    dataCharts={{
                      data,
                      name: `Визиты (ПланФакт) ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'visit'
                    }} />
                </li>
                <li>
                  <HeaderBtn
                    content="Настройка цвета"
                    purpose="changeColor"
                    modalTitle="Настройка цвета"
                    dataCharts={{ type: "visit", labels, isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>}
          </div>
        </div>
      </div>
      {isloader ?
        <ChartLoader />
        : !dataVisitCharts?.length
          ? <p className='charts__text'>Отчёты в выбранных вами фильтрах не найдены. Убедитесь, что вы указали верно данные в фильтрах, расширьте рассматриваемый период  или географический охват.
          </p>
          : <>
            <ChartComponent
              type={type}
              data={data}
              isHiddenItem={isHiddenLabel}
              options={actualOptionsChart}
              isIncreasedSize={isIncreasedSize}
              loader={isloader}
              chartType={'visit'}
              colorOptions={colorList}
            />
            <ScrollingLegend
              colorOptions={colorList}
              handleClickOnLegend={handleClickOnLegend}
              isIncreasedSize={isIncreasedSize}
              isVisiblelabel={isVisiblelabel}
              isHiddenLabel={isHiddenLabel}
              isLabels={labels}
              type={type}
            />
          </>
      }
    </div>
  )
};

export default VisitsByDaysChart;